import Cookies from 'js-cookie';
import paymentApi from '../../services/paymentApi';

/**
 * Token do jogador.
 *
 * O módulo auth guarda o JWT em `state.token`; este arquivo procurava
 * `rootState.auth.jwt`, que nunca existiu — `createCheckout` morria em
 * "Authentication required" antes de chamar o servidor, e o botão de comprar
 * Paralleliums não fazia nada. O cookie é a segunda opção porque é de lá que o
 * auth reidrata a sessão ao recarregar a página.
 */
function getToken(rootState) {
  return rootState.auth?.token || Cookies.get('jwt') || null;
}

const state = {
  packages: [],
  promotions: [],
  sidebarPromotions: [],
  popupPromotion: null,
  loading: false,
  error: null,
  purchaseHistory: []
};

const getters = {
  /**
   * Get active promotions
   */
  activePromotions: (state) => state.promotions,

  /**
   * Get packages with promotions applied
   */
  packagesWithPromotions: (state) => {
    return state.packages.map(pkg => {
      // Find promotions that include this package
      const applicablePromotions = state.promotions.filter(promo =>
        promo.packages && promo.packages.some(p => p.id === pkg.id)
      );

      // Calculate total bonus
      // O teto de 100% e a mesma regra do servidor (api::promotion.calculateBonus).
      // Sem ele, o mesmo pacote em varias promocoes anunciava na vitrine um bonus
      // maior do que o que seria creditado de verdade na compra.
      const somaBruta = applicablePromotions.reduce(
        (sum, promo) => sum + (promo.bonusPercentage || 0),
        0
      );
      const totalBonusPercentage = Math.max(0, Math.min(100, somaBruta));

      const bonusAmount = Math.floor((pkg.amount * totalBonusPercentage) / 100);
      const totalAmount = pkg.amount + bonusAmount;

      return {
        ...pkg,
        hasPromotion: applicablePromotions.length > 0,
        promotions: applicablePromotions,
        bonusPercentage: totalBonusPercentage,
        bonusAmount: bonusAmount,
        totalAmount: totalAmount,
        // totalAmount 0 (pacote mal cadastrado) daria Infinity na vitrine.
        pricePerParallelium: totalAmount > 0 ? (pkg.price / totalAmount).toFixed(4) : '0.0000'
      };
    });
  },

  /**
   * Get best value package (lowest price per parallelium)
   */
  bestValuePackage: (state, getters) => {
    const packages = getters.packagesWithPromotions;
    if (packages.length === 0) return null;

    return packages.reduce((best, current) => {
      const bestPrice = parseFloat(best.pricePerParallelium);
      const currentPrice = parseFloat(current.pricePerParallelium);
      return currentPrice < bestPrice ? current : best;
    });
  },

  /**
   * Get sidebar promotions
   */
  sidebarPromotions: (state) => state.sidebarPromotions,

  /**
   * Get popup promotion
   */
  popupPromotion: (state) => state.popupPromotion,

  /**
   * Check if loading
   */
  isLoading: (state) => state.loading
};

const mutations = {
  SET_PACKAGES(state, packages) {
    state.packages = packages;
  },

  SET_PROMOTIONS(state, promotions) {
    state.promotions = promotions;
  },

  SET_SIDEBAR_PROMOTIONS(state, promotions) {
    state.sidebarPromotions = promotions;
  },

  SET_POPUP_PROMOTION(state, promotion) {
    state.popupPromotion = promotion;
  },

  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  SET_ERROR(state, error) {
    state.error = error;
  },

  SET_PURCHASE_HISTORY(state, history) {
    state.purchaseHistory = history;
  },

  CLEAR_ERROR(state) {
    state.error = null;
  }
};

const actions = {
  /**
   * Fetch parallelium packages
   */
  async fetchPackages({ commit, rootState }) {
    try {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');

      const locale = rootState.Configs?.selectedLanguage || 'pt';
      const packages = await paymentApi.getParalleliumPackages(locale);

      commit('SET_PACKAGES', packages);
      commit('SET_LOADING', false);

      return packages;
    } catch (error) {
      commit('SET_ERROR', error.message);
      commit('SET_LOADING', false);
      throw error;
    }
  },

  /**
   * Fetch active promotions
   */
  async fetchPromotions({ commit }) {
    try {
      const promotions = await paymentApi.getActivePromotions();
      commit('SET_PROMOTIONS', promotions);
      return promotions;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  /**
   * Fetch sidebar promotions
   */
  async fetchSidebarPromotions({ commit }) {
    try {
      const promotions = await paymentApi.getSidebarPromotions();
      commit('SET_SIDEBAR_PROMOTIONS', promotions);
      return promotions;
    } catch (error) {
      console.error('Error fetching sidebar promotions:', error);
      commit('SET_SIDEBAR_PROMOTIONS', []);
    }
  },

  /**
   * Fetch popup promotion
   */
  async fetchPopupPromotion({ commit }) {
    try {
      const promotion = await paymentApi.getPopupPromotion();
      commit('SET_POPUP_PROMOTION', promotion);
      return promotion;
    } catch (error) {
      console.error('Error fetching popup promotion:', error);
      commit('SET_POPUP_PROMOTION', null);
    }
  },

  /**
   * Create checkout session and redirect
   */
  async createCheckout({ rootState }, packageId) {
    try {
      const token = getToken(rootState);

      if (!token) {
        throw new Error('Authentication required');
      }

      const session = await paymentApi.createCheckoutSession(packageId, token);

      // Redirect to Stripe Checkout
      if (session.url) {
        window.location.href = session.url;
      }

      return session;
    } catch (error) {
      console.error('Error creating checkout:', error);
      throw error;
    }
  },

  /**
   * Verify purchase after redirect
   */
  async verifyPurchase({ rootState }, sessionId) {
    try {
      const token = getToken(rootState);

      if (!token) {
        throw new Error('Authentication required');
      }

      const purchase = await paymentApi.verifyPurchase(sessionId, token);
      return purchase;
    } catch (error) {
      console.error('Error verifying purchase:', error);
      throw error;
    }
  },

  /**
   * Fetch purchase history
   */
  async fetchPurchaseHistory({ commit, rootState }) {
    try {
      const token = getToken(rootState);

      if (!token) {
        return [];
      }

      const history = await paymentApi.getPurchaseHistory(token);
      commit('SET_PURCHASE_HISTORY', history);
      return history;
    } catch (error) {
      console.error('Error fetching purchase history:', error);
      commit('SET_PURCHASE_HISTORY', []);
      return [];
    }
  },

  /**
   * Initialize monetization data (packages + promotions)
   */
  async initializeMonetization({ dispatch }) {
    try {
      await Promise.all([
        dispatch('fetchPackages'),
        dispatch('fetchPromotions'),
        dispatch('fetchSidebarPromotions'),
        dispatch('fetchPopupPromotion')
      ]);
    } catch (error) {
      console.error('Error initializing monetization:', error);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
